import { inject, Injector } from '@angular/core';
import { assert } from '@sama/js-core';
import { Observable } from 'rxjs';
import { debounceTime, filter, map } from 'rxjs/operators';
import { EventBatchValues } from '../../../cross-team-shared/event-tracking/event-batch-values';
import {
  buildTrackerFromShellConnector,
  TrackingEvent,
} from '../../../cross-team-shared/event-tracking/event-utils';
import { selectionToArray } from '../../../cross-team-shared/misc-utils';
import { shellConnectorToken } from '../../../team-apis/core/shell-connector';
import { Point2D } from '../../../team-apis/otterdam/answers/shape-typings';
import { AbstractSensorContentStore2D } from '../state/abstract-sensor-content-store-2d';
import { AbstractVectorTranslationHelper } from '../tools/shared/drag-handlers/abstract-vector-translation-helper';
import { AbstractKeyboardMovementService } from './abstract-keyboard-movement-service';

export abstract class AbstractTranslationService extends AbstractKeyboardMovementService {
  protected readonly store = inject(AbstractSensorContentStore2D);
  protected readonly injector = inject(Injector);
  private readonly tracker = buildTrackerFromShellConnector(
    inject(shellConnectorToken),
  );

  private batchValues?: EventBatchValues;
  protected abstract method: string;

  protected setUpKeyboardShortcuts(
    translationHelperEvent$: Observable<AbstractVectorTranslationHelper>,
  ) {
    if (this.store.state().readOnly) {
      return;
    }

    this.subscribeTo(
      translationHelperEvent$.pipe(debounceTime(500)),
      (translationHelper) => {
        assert(
          this.batchValues,
          'Batch values must be set before tracking an event!',
        );
        translationHelper.pushAndTrackEvent({
          ...this.batchValues.buildEventTrackingValue(),
          source: 'keyboard_shortcut',
          method: this.method,
        });
        this.batchValues = undefined;
      },
    );
  }

  protected canTranslate() {
    return !!selectionToArray(this.store.state().selection).length;
  }

  protected createTranslationHelper$(
    offset$: Observable<{ delta: Point2D; isSmallNudge?: boolean }>,
  ) {
    return offset$.pipe(
      // if no shape is selected, do nothing
      filter(() => this.canTranslate()),
      map(({ delta, isSmallNudge }) => {
        // the translation helper caches all movable info, so we need to
        // create a new instance everytime because the selection, shape,
        // visibility or frame could have changed
        const translationHelper = this.getTranslationHelper(delta);

        this.batchValues ??= this.startBatch(translationHelper.event);
        if (isSmallNudge) {
          this.batchValues.addContext({
            batch_included_small_nudge: isSmallNudge,
          });
        }

        translationHelper.move(delta);
        this.batchValues.endEvent();

        return translationHelper;
      }),
    );
  }

  private startBatch(event: TrackingEvent) {
    const batchValues = new EventBatchValues(
      {
        ...event,
      },
      this.tracker,
    );
    batchValues.startEvent();
    return batchValues;
  }

  protected abstract getTranslationHelper(
    offset: Point2D,
  ): AbstractVectorTranslationHelper;
}
